import React, { useRef, useState, useLayoutEffect, useImperativeHandle, forwardRef } from "react"
import { useOutletContext } from 'react-router-dom'
import { useTranslation } from "react-i18next"
import { ToggleButtonGroup, ToggleButton } from '@mui/material'
import { Overlay } from "ol"
import { Vector as VectorSource } from "ol/source"
import { Vector as VectorLayer } from "ol/layer"
import { Fill, Style, Circle as CircleStyle, Stroke } from "ol/style"
import { easeOut } from 'ol/easing'
import { getVectorContext } from 'ol/render'
import { unByKey } from 'ol/Observable'

import useLoop from "Shared/Hooks/useLoop"
import Map from "Shared/Components/Map"

import { getIconStyle, setLayerSource, getBaseMap } from 'Pages/Portal/SmartDevice/Smartwatch/components/utils'
import 'Pages/Portal/SmartDevice/Smartwatch/components/style.css'

const OverlayContent = ({ position, opened }) => {
  const { t } = useTranslation()

  return (
    <div style={{ overflow: "auto" }}>
      <div style={{ fontSize: "12px" }}><b>{opened["device_name"]}</b></div>
      <div style={{ fontSize: "12px" }}>{`${t("proximity_plantType")}: ${opened["plant_type"] ?? "-"}`}</div>
    </div>
  )
}

const MapDisplay = forwardRef(({ focus }, ref) => {
  const [opened, setOpened] = useState()

  const mapRef = useRef()
  const overlayRef = useRef()

  useLayoutEffect(() => {
    const deviceLayer = new VectorLayer({
      source: new VectorSource({}),
      style: function (feature) { 
        const online = feature.get("status")
        const icon = `${feature.get("icon_type")}_${online ? "online" : "offline"}`

        return [
          getIconStyle(icon, 30, 10)
        ]
      }
    })

    deviceLayer.set('name', 'deviceLayer')

    const map = getBaseMap([deviceLayer])

    const overlay = new Overlay({
      element: overlayRef.current,
      offset: [25, 0],
    })

    map.addOverlay(overlay)

    map.on('click', function (e) {
      const feature_id = map.forEachFeatureAtPixel(e.pixel, function (f) {
        return f.getId()
      } , {
        layerFilter: function (layer) {
          return layer.get('name') === 'deviceLayer'
        }
      })

      if (feature_id == null) {
        resetPopup()
        return
      }

      focus(feature_id, false)
    })

    mapRef.current = {
      "map": map,
      "deviceLayer": deviceLayer,
      "overlay": overlay
    }
  }, [])

  const zoomToFeature = (id, feature, zoom) => {
    if (mapRef.current.selected !== id) {
      const map = mapRef.current.map

      setOpened({
        "device_name": feature.get("name"),
        "plant_type": feature.get("plant_type")
      })

      if ((feature.get("location")["lon"] === 0 || feature.get("location")["lon"] == null) || feature.get("location")["lat"] === 0 || feature.get("location")["lat"] == null) {      
        resetPopup()
        return
      }

      const coor = feature.getGeometry().getCoordinates()

      mapRef.current.overlay.setPosition(coor)

      if (zoom) {
        const view = map.getView()

        view.animate({
          center: coor,
          duration: 2000,
          zoom: 20
        })
      }
    }
  }

  const resetPopup = () => {
    mapRef.current.overlay.setPosition([0, 0])
    setOpened()
  }

  useImperativeHandle(ref, () => ({
    getFeature: (id) => {      
      return mapRef.current.deviceLayer.getSource().getFeatureById(id)
    },
    zoomToFeature: (id, feature, content, zoom) => {
      zoomToFeature(id, feature, content, zoom)
    },
    updateData: (data) => {
      const map = mapRef.current.map

      if (map) {
        setLayerSource(mapRef.current.deviceLayer, data)
      }
    },
    closePopup: () => {
      resetPopup()
    },
    setMapExtent: (coordinate) => {
      mapRef.current.map.getView().fit([coordinate["x1"], coordinate["y1"], coordinate["x2"], coordinate["y2"]], mapRef.current.map.getSize())
    }
  }))

  return (<>
    <Map mapRef={mapRef}/>

    <div ref={overlayRef} className="popup" style={{ padding: "10px" }}>
      {opened && <OverlayContent opened={opened}/>}
    </div>
  </>)
})

export default MapDisplay
